/**
=========================================================
* Trust Cargo - a table as an Excel file
=========================================================

* The rows a screen is showing, downloaded as .xlsx - every row the filters
* and the search let through, in the order the table has them, not just the
* page that is open.
*
* The columns are handed in by the screen as {header, value(row)}, so the file
* says exactly what the table says: the same headings, the same figures, in
* the same order. Numbers stay numbers, so a column can be summed in Excel
* straight away; a cell with nothing in it stays empty rather than "-".
*
* sheetjs-style is already in the app (the shipping marks are read with it),
* so this adds nothing to carry.
*/

import XLSX from "sheetjs-style";

/** YYYY-MM-DD in local time - the date in the file name. */
export const stamp = (d = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** What goes in a cell: numbers as numbers, nothing as an empty cell. */
const cellValue = (v) => {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "number") return Number.isFinite(v) ? v : "";
  return v;
};

/**
 * Build the workbook and hand it to the browser as a download.
 *
 * `xlsx` is only there so a test can watch what is written without a real
 * download; the screens never pass it.
 */
export default function exportToExcel({
  fileName,
  sheetName = "Sheet1",
  columns,
  rows,
  xlsx = XLSX,
}) {
  const header = columns.map((c) => c.header);
  const body = rows.map((row) => columns.map((c) => cellValue(c.value(row))));

  const sheet = xlsx.utils.aoa_to_sheet([header, ...body]);

  // wide enough for the longest value in each column, within reason
  sheet["!cols"] = columns.map((c, i) => {
    const longest = body.reduce(
      (w, r) => Math.max(w, String(r[i] === undefined ? "" : r[i]).length),
      String(c.header).length
    );
    return { wch: Math.min(40, longest + 2) };
  });

  const book = xlsx.utils.book_new();
  // Excel refuses a sheet name longer than 31 characters
  xlsx.utils.book_append_sheet(book, sheet, String(sheetName).slice(0, 31));

  const name = /\.xlsx$/i.test(fileName) ? fileName : `${fileName}.xlsx`;
  xlsx.writeFile(book, name);

  return { rows: body.length, fileName: name };
}
